import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { setSignedIn, track } from '../lib/analytics';

const AuthContext = createContext(null);

const TOKEN_KEY = 'user_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem(TOKEN_KEY);
  const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
  const removeToken = () => localStorage.removeItem(TOKEN_KEY);

  const fetchUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      removeToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (!loading) setSignedIn(!!user);
  }, [loading, user]);

  const signIn = (data, method) => {
    setSignedIn(true);
    track('sign_in_completed', { method });
    setToken(data.token);
    setUser(data.user);
    return { success: true, user: data.user };
  };

  const failure = (err, fallback) => {
    const errorMessage = err.response?.data?.error || fallback;
    setError(errorMessage);
    return { success: false, error: errorMessage, ...err.response?.data, status: err.response?.status };
  };

  // Resolves to { success, sent } once a code is emailed, or signs straight in under dev login.
  const requestCode = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/login/request`, { email });
      if (response.data.token) return signIn(response.data, 'dev');
      track('sign_in_code_sent');
      return { success: true, sent: true };
    } catch (err) {
      return failure(err, 'Could not send a code');
    }
  };

  const verifyCode = async (check) => {
    try {
      setError(null);
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/login/verify`, check);
      return signIn(response.data, check.token ? 'link' : 'code');
    } catch (err) {
      track('sign_in_failed', { method: check.token ? 'link' : 'code' });
      return failure(err, 'Sign in failed');
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/register`, data);

      if (response.data.success) {
        if (response.data.token) {
          setToken(response.data.token);
        }
        if (response.data.user) {
          setUser(response.data.user);
        }
        return {
          success: true,
          message: response.data.message,
          loggedIn: !!response.data.token,
          codeSent: !!response.data.codeSent,
          email: response.data.email,
          user: response.data.user
        };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
  };

  const setTicketFollowState = useCallback((ticketId, shouldFollow) => {
    setUser((prev) => {
      if (!prev) return prev;
      const followed = prev.followedTickets || [];
      const isFollowing = followed.includes(ticketId);
      if (shouldFollow === isFollowing) return prev;
      return {
        ...prev,
        followedTickets: shouldFollow
          ? [...followed, ticketId]
          : followed.filter((id) => id !== ticketId)
      };
    });
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.admin || false;
  const isActive = user?.isActive || false;

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isActive,
    requestCode,
    verifyCode,
    register,
    logout,
    getToken,
    refreshUser: fetchUser,
    setTicketFollowState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
